import { t } from '@/plugins/i18n'
import { useSite2Code, useSite } from '@/store'
import { computed } from 'vue'
import { COUNTRY } from '@/global'
const memberNames = {
  LEVEL1: '经济舱',
  LEVEL2: '标准舱',
  LEVEL3: '商务舱',
  SUPER: '头等舱',
  SPECIAL: '专属会员'
}
/** 韩国站以及英国站对应的会员等级 */
const krOrUkMemberNames = {
  LEVEL1: '经济舱',
  LEVEL2: '商务舱',
  LEVEL3: '头等舱'
}
/** 短信类型 */
enum BizType {
  SIGN = 1,
  RESET = 2,
  LOGIN = 3,
  EDIT = 5
}

export const countryCodeNameMap = new Map()
countryCodeNameMap.set(COUNTRY.KR, t('韩国'))
countryCodeNameMap.set(COUNTRY.CN, t('中国'))

/** 用户类型 */
enum UseType {
    /** B2B */
    b2b = 2,
    /** D2C */
    d2c = 1,
    /** D2D */
    d2d = 3
}

type PlatProps = keyof typeof UseType
const smc = {
  getUrl: () => `/smc/promotion/index?redirect=MyPromotion&lang=${localStorage.__lang__}`
}

/**
 * 国家及货币单位
 */
enum Site {
  /**
   * 日本站
   */
  JA = 'JapanStation',
  /**
   * 韩国站
   */
  KO = 'KoreaStation',
  /**
   * 英国站
   */
  EN = 'UkStation',
}

/**
 * 站点对应的货币单位
 */
const siteMapMonetaryUnit = new Map<string, string>()
siteMapMonetaryUnit.set(Site.JA, '円')
siteMapMonetaryUnit.set(Site.KO, '₩')
siteMapMonetaryUnit.set(Site.EN, '$')

/**
 * 站点的导航条时区名字
 */
const siteMapAreaName = new Map<string, string>()
siteMapAreaName.set(Site.JA, 'Tokyo') // 日本
siteMapAreaName.set(Site.KO, 'Seoul') // 韩国
siteMapAreaName.set(Site.EN, 'GMT') // GMT +n

/**
 * 国家枚举
 */
enum CountriesEnum {
  /**
   * 日本
   */
  Japan = 'JP',
  /**
   * 澳大利亚
   */
  Australia = 'AU',
  /**
   * 加拿大
   */
  Canada = 'CA',
  /**
   * 爱尔兰
   */
  Ireland = 'IE',

  /**
   * 韩国
   */
  Korea = 'KR',
  /**
   * 马来西亚
   */
  Malaysia = 'MY',
  /**
   * 新西兰
   */
  NewZealand = 'NZ',
  /**
   * 新加坡
   */
  Singapore = 'SG',
  /**
   * 英国
   */
  UK = 'GB',
  /**
   * 其他国家
   */
  other = 'OTHER',
}

// 查找国家国旗资源
const findCountryNativeFlag = (name: string) => {
  name = name || useSite2Code()
  return require(`@/assets/image/country/flag_${name}_tab.png`)
}

type countryType = {
  key: CountriesEnum,
  desc: string;
  navigatorLang: string[];
  mobileAreaCode: string;
}

/**
 * 国家对应的一些信息
 */
const countries = [
  {
    key: CountriesEnum.Japan,
    desc: '日本',
    navigatorLang: ['ja'],
    mobileAreaCode: '+81',
    time: 'UTC+9'
  },
  {
    key: CountriesEnum.Korea,
    desc: '한국',
    navigatorLang: ['ko'],
    mobileAreaCode: '+82',
    time: 'UTC+9'
  },
  {
    key: CountriesEnum.Australia,
    desc: 'Australia',
    navigatorLang: ['en-AU', 'en'],
    mobileAreaCode: '+61',
    time: 'UTC+10'
  },
  {
    key: CountriesEnum.Canada,
    desc: 'Canada',
    navigatorLang: ['en-CA', 'fr-CA'],
    mobileAreaCode: '+1',
    time: 'UTC-5'
  },
  {
    key: CountriesEnum.Ireland,
    desc: 'Ireland',
    navigatorLang: ['en-IE', 'en'],
    mobileAreaCode: '+353',
    time: 'UTC+0'
  },

  {
    key: CountriesEnum.Malaysia,
    desc: 'Malaysia',
    navigatorLang: ['en-MY', 'en', 'ms-MY'],
    mobileAreaCode: '+60',
    time: 'UTC+8'
  },
  {
    key: CountriesEnum.NewZealand,
    desc: 'NewZealand',
    navigatorLang: ['en-NZ', 'en', 'zh-SG'],
    mobileAreaCode: '+64',
    time: 'UTC+12'
  },
  {
    key: CountriesEnum.Singapore,
    desc: 'Singapore',
    navigatorLang: ['en-SG', 'zh-SG', 'en', 'zh'],
    mobileAreaCode: '+65',
    time: 'UTC+8'
  },
  {
    key: CountriesEnum.UK,
    desc: 'United Kingdom',
    navigatorLang: ['en', 'en-GB'],
    mobileAreaCode: '+44',
    time: 'UTC+0'
  },
  {
    key: CountriesEnum.other,
    desc: 'Other Countries And Regions',
    navigatorLang: ['*'],
    mobileAreaCode: ''
  }
]

/**
 * 日本站点下的国家
 */
const japanStationCor2Country: string[] = [CountriesEnum.Japan]
/**
 * 韩国站点下的国家
 */
const koreaStationCor2Country: string[] = [CountriesEnum.Korea]
/**
 * 英国站点下的国家
 */
const ukStationCor2Country: string[] = [
  CountriesEnum.Australia,
  CountriesEnum.Canada,
  CountriesEnum.Ireland,
  CountriesEnum.Malaysia,
  CountriesEnum.NewZealand,
  CountriesEnum.Singapore,
  CountriesEnum.UK,
  CountriesEnum.other
]

/**
 *  判断那个国家属于哪个
 */
const countryMapSite = new Map<string, Site>()
Object.keys(CountriesEnum).forEach((countryKey) => {
  const isJapanStationCountry = japanStationCor2Country.includes(countryKey)
  const isKoreaStationCountry = koreaStationCor2Country.includes(countryKey)
  const isUKStationCounyty = ukStationCor2Country.includes(countryKey)

  if (isJapanStationCountry) {
    countryMapSite.set(countryKey, Site.JA)
  }
  if (isKoreaStationCountry) {
    countryMapSite.set(countryKey, Site.KO)
  }
  if (isUKStationCounyty) {
    countryMapSite.set(countryKey, Site.EN)
  }
})

// 国家货币
const countryCurrency = new Map<string, string>()
countryCurrency.set(Site.JA, 'JPY')
countryCurrency.set(Site.KO, 'KRW')
countryCurrency.set(Site.EN, 'USD')

enum Term {
  /** 利用规约 */
  utilize = 'utilize',
  /** 隐私权限 */
  privacy = 'privacy',
  /** 次转正提醒 */
  regular = 'regular',
  /** OEM条款 */
  oem = 'oem',
}

/**
 * 第三授权平台方枚举
 */
enum Plat {

  /** facebook */
  FB = 'facebook',
  /** google  */
  GG = 'google',
  /** Line */
  LE = 'line',
  /** apple */
  AP = 'apple',
  /** yahoo */
  YH = 'yahoo',
  /** tiktok */
  TK = 'tiktok',
  /** 韩国KAKAO */
  KK = 'kakao',
  /** 韩国naver */
  NV = 'naver',
}

const tripartitePlatforms = [
  {
    key: Plat.AP,
    logoUrl: 'https://static-s.theckb.com/BusinessMarket/Client/apple.png',
    country: []
  },
  {
    key: Plat.FB,
    logoUrl: 'https://static-s.theckb.com/BusinessMarket/Client/facebook.png',
    country: []
  },
  {
    key: Plat.GG,
    logoUrl: 'https://static-s.theckb.com/BusinessMarket/Client/google.png',
    country: ['ko', 'ja', 'zh', 'en']
  },
  {
    key: Plat.KK,
    logoUrl: 'https://static-jp.theckb.com/static-asset/client/kaokaofav.png',
    country: ['ko']
  },
  {
    key: Plat.NV,
    logoUrl: 'https://static-jp.theckb.com/static-asset/client/nFavicon96.png',
    country: []
  },

  {
    key: Plat.LE,
    logoUrl: 'https://static-s.theckb.com/BusinessMarket/Client/line.png',
    country: ['ja', 'zh']
  },
  {
    key: Plat.TK,
    logoUrl: 'https://static-s.theckb.com/BusinessMarket/Client/tiltok.png',
    country: []
  },
  {
    key: Plat.YH,
    logoUrl: 'https://static-s.theckb.com/BusinessMarket/Client/yahoo.png',
    country: []
  }
]

const compTripartitePlatforms = computed(() => tripartitePlatforms.filter(i => i.country.includes(useSite())))

/**
 * 未登录时，根据浏览器语言，返回用户所在站点
 * @returns Site
 */
const getSiteByNavigatorLang = (): Site => {
  const lang = navigator.language
  for (let i = 0; i < countries.length; i++) {
    const country = countries[i]
    // 若浏览器语言在某个国家语言list中，则返回改国家对应的站点。没有则是其他国家(英国站)
    if (country.navigatorLang.includes(lang)) {
      const countryKey = country.key
      const site = countryMapSite.get(countryKey) as Site
      return site
    }
    return Site.EN
  }

  return Site.EN
}

const lineUrl = 'https://line.me/R/ti/p/@962puoaf?oat_content=url'

const serverEmail = () => {
  const email:any = {
    ja: 'info@theckb.com',
    ko: 'Korea@theckb.com',
    en: 'Inquiry@theckb.com'
  }
  return email[useSite()] ?? ''
}

const serverPhone = () => {
  const phone:any = {
    ja: '03-4446-7313',
    ko: '010-7352-2830',
    en: '+86 13783507302'
  }
  return phone[useSite()] ?? ''
}
// 表格下单模版
const excelOrderTemplateHref:any = {
  ja: {
    d2cOrder: 'https://static-jp.theckb.com/static-asset/client/excelTemplate/一括注文用テンプレート (D2C直送).xlsx',
    d2cStock: 'https://static-jp.theckb.com/static-asset/client/excelTemplate/一括アップロード用テンプレート（D2C在庫保有）.xlsx',
    b2bStock: 'https://static-jp.theckb.com/static-asset/client/excelTemplate/一括アップロード用テンプレート（B2B）.xlsx',
    shipping: 'https://static-jp.theckb.com/static-asset/client/excelTemplate/テンプレート_その他.xlsx'
  },
  ko: {
    d2cOrder: 'https://static-s.theckb.com/BusinessMarket/Client/excelTemplate/ko/시트주문템플릿.xlsx',
    d2cStock: 'https://static-s.theckb.com/BusinessMarket/Client/excelTemplate2/ko/SKU 일괄 업로드용 템플릿(D2C).xlsx',
    b2bStock: 'https://static-s.theckb.com/BusinessMarket/Client/excelTemplate2/ko/SKU 일괄 업로드용 템플릿(B2B).xlsx',
    shipping: 'https://static-s.theckb.com/BusinessMarket/Client/shipping/템플릿_기타.xlsx'
  },
  en: {
    d2cOrder: 'https://static-s.theckb.com/BusinessMarket/Client/excelTemplate/en/Bulk Order Template (D2C Send directly to end customer).xlsx',
    d2cStock: 'https://static-s.theckb.com/BusinessMarket/Client/excelTemplate/en/Batch upload templates (D2C Inventory Holding）.xlsx',
    b2bStock: 'https://static-s.theckb.com/BusinessMarket/Client/excelTemplate/en/Bulk Upload Template (B2B).xlsx',
    shipping: 'https://static-s.theckb.com/BusinessMarket/Client/shipping/Shipping template.xlsx'
  }
}

enum ShopType {
  BASE = 1,
  SHOPIFY = 2,
  STORE = 3,
  AMAZON = 4,
  CAFE = 6
}
const apiShopName:any = {
  [ShopType.BASE]: 'Base',
  [ShopType.SHOPIFY]: 'Shopify',
  [ShopType.STORE]: 'STORES',
  [ShopType.AMAZON]: 'Amazon',
  [ShopType.CAFE]: 'Cafe24'
}
const apiShopIcon:any = {
  [ShopType.BASE]: 'https://static-s.theckb.com/BusinessMarket/Client/associated/base.png',
  [ShopType.SHOPIFY]: 'https://static-s.theckb.com/BusinessMarket/Client/associated/shopify.png',
  [ShopType.STORE]: 'https://static-s.theckb.com/BusinessMarket/Client/associated/stores.png',
  [ShopType.AMAZON]: 'Amazon',
  [ShopType.CAFE]: 'https://static-s.theckb.com/BusinessMarket/Client/associated/cafe.png'
}

const abnormalDegreeDescList = {
  serious: t('重度'),
  medium: t('中度'),
  lightly: t('轻度'),
  '306_1_5': t('1-5cm'),
  '306_6_10': t('6-10cm'),
  '306_over10': t('10cm以上'),
  101: t('到错货'),
  102: t('少件'),
  103: t('多件'),
  104: t('快递扫不出'),
  201: t('$颜色差异$9'),
  202: t('$商品上有中文$9'),
  203: t('$配件差异$9'),
  204: t('$logo差异$9'),
  205: t('$尺码差异$9'),
  206: t('$设计差异$9'),
  207: t('$材质、面料差异$9'),
  301: t('色差'),
  302: t('污渍/斑点/水印'),
  303: t('破损'),
  304: t('线头'),
  305: t('接缝不良'),
  306: t('抽丝'),
  307: t('针眼'),
  308: t('霉菌'),
  309: t('生锈'),
  310: t('商品有中文'),
  311: t('纽扣破损'),
  312: t('拉链破损'),
  313: t('附属品缺陷'),
  314: t('有气味'),
  315: t('其他不良'),
  316: t('$通电不良$9')
} as any

export {
  PlatProps,
  Term,
  smc,
  memberNames,
  krOrUkMemberNames,
  Site,
  BizType,
  siteMapMonetaryUnit,
  countries,
  countryMapSite,
  CountriesEnum, countryType,
  countryCurrency,
  japanStationCor2Country,
  koreaStationCor2Country,
  ukStationCor2Country,
  siteMapAreaName,
  tripartitePlatforms,
  findCountryNativeFlag,
  getSiteByNavigatorLang,
  compTripartitePlatforms,
  lineUrl,
  Plat,
  UseType,
  serverEmail,
  serverPhone,
  excelOrderTemplateHref,
  apiShopName,
  apiShopIcon,
  abnormalDegreeDescList
}

/** 自动续费开启状态 */
export enum ENUM_RENEWAL_STATUS {
    /** 已开启 */
    OPEN = 1,
    /** 未开启 */
    CLOSE,
}

/** 自动续费开启方式 */
export enum ENUM_RENEWAL_STYLE {
    /** 余额 */
    BALANCE = 1,
    /** Paypal */
    Paypal,
    /** 信用卡 */
    CREDIT_CARD
}
